/**
 * Helpers that keep the scoring screen focused on the round that the host
 * scores now. They only read scores and standings. They do not change how
 * scores are stored or how standings are computed.
 *
 * Round numbers here are 1-based. They match score.roundIndex.
 */
import type { PointsConfig, ScoreRecord, ScoringQuestion, TeamStanding } from '../../utils/standings';

/** Minimal round shape. orderIndex is 0-based. */
export interface FocusRound {
  orderIndex: number;
  roundType: 'STANDARD' | 'VISUAL' | 'AUDIO';
  pointsPerQuestion?: number | null;
  // points null = the round default.
  questions: readonly ScoringQuestion[];
}

/** Minimal team shape. */
export interface FocusTeam {
  id: string;
  leftEarly?: boolean;
}

/**
 * Returns true when the team has a graded answer for the round.
 * A QUESTION, VISUAL, or ROUND_TOTAL row counts. A BONUS row alone does not.
 */
export function isRoundScored(
  scores: readonly ScoreRecord[],
  teamId: string,
  roundIndex: number,
): boolean {
  return scores.some(
    (s) =>
      s.teamId === teamId &&
      s.roundIndex === roundIndex &&
      (s.scoreType === 'QUESTION' || s.scoreType === 'VISUAL' || s.scoreType === 'ROUND_TOTAL'),
  );
}

/**
 * Picks the round to open when the scoring screen loads.
 * It is the highest round that has a score. With no scores, it is the first round.
 * A late sheet for an earlier round does not move it back.
 * Returns null when the quiz has no rounds.
 */
export function detectCurrentRound(
  scores: readonly ScoreRecord[],
  roundNumbers: readonly number[],
): number | null {
  if (roundNumbers.length === 0) return null;
  const known = new Set(roundNumbers);
  let highest: number | null = null;
  for (const s of scores) {
    if (!known.has(s.roundIndex)) continue;
    if (highest === null || s.roundIndex > highest) highest = s.roundIndex;
  }
  return highest ?? Math.min(...roundNumbers);
}

/** Question points that one team can earn in one round, before bonus and doubling. */
export function roundQuestionMax(round: FocusRound, pointsConfig: PointsConfig): number {
  const roundIndex = round.orderIndex + 1;
  // Visual rounds are one point per image. buildPointsConfig already sets 1 for them.
  if (round.roundType === 'VISUAL') return round.questions.length;
  const ppq = pointsConfig[roundIndex] ?? 2;
  // Each question counts its own value, or the round default.
  return round.questions.reduce((n, q) => n + (q.points ?? ppq), 0);
}

/**
 * Question-correct percentage for one team, over the rounds that `include` accepts
 * and that the team has a score for. Rounds not played yet do not count.
 * Bonus and doubling do not count. Returns null when no included round is scored.
 */
export function teamQuestionPct(
  standing: TeamStanding | undefined,
  rounds: readonly FocusRound[],
  pointsConfig: PointsConfig,
  scores: readonly ScoreRecord[],
  include: (roundIndex: number) => boolean,
): number | null {
  if (!standing) return null;
  let earned = 0;
  let max = 0;
  for (const round of rounds) {
    const roundIndex = round.orderIndex + 1;
    if (!include(roundIndex)) continue;
    if (!isRoundScored(scores, standing.teamId, roundIndex)) continue;
    const rs = standing.roundScores.find((r) => r.roundIndex === roundIndex);
    earned += rs?.questionPoints ?? 0;
    max += roundQuestionMax(round, pointsConfig);
  }
  if (max === 0) return null;
  return Math.round((earned / max) * 1000) / 10;
}

/** Number of active teams that have a score for the round, and the number of active teams. */
export function roundProgress(
  teams: readonly FocusTeam[],
  scores: readonly ScoreRecord[],
  roundIndex: number,
): { scored: number; total: number } {
  const active = teams.filter((t) => !t.leftEarly);
  const scored = active.filter((t) => isRoundScored(scores, t.id, roundIndex)).length;
  return { scored, total: active.length };
}
